import { EMPTY_PREBID_MODULE, MultipleEditPayload, PrebidModule, PrebidModuleData, PrebidModuleDataBySite } from '@pubstack/common/src/prebidModule'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useIdentityModule } from '~/api/adm-api.hook'
import { usePrebidModulesCatalog } from '~/api/catalog.api'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdmPrebidModulePage } from '~/modules/adm/inventory/modules/PureAdmPrebidModulePage'
import { configurationState } from '~/state'

export const AdmIdentityModulePage = () => {
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const [identityData, setIdentityData] = useState<PrebidModuleDataBySite<'Identity'>[]>([])
  const [prebidModule, setPrebidModule] = useState<PrebidModule>(EMPTY_PREBID_MODULE)

  const breadcrumbs = useBreadcrumbs({ integrationName: prebidModule.displayName ?? '' })
  const toast = useToast()
  const navigate = useNavigate()

  const { moduleId } = useParams()
  const { all, byId } = useIdentityModule(null)
  const { byId: catalogById, isLoading: isCatalogLoading } = usePrebidModulesCatalog()

  const updateSiteIdentity = async (data: PrebidModuleDataBySite<'Identity'>) => {
    try {
      if (moduleId) {
        const update = { name: prebidModule.code, ...data.moduleData }
        await byId.put(moduleId, data.siteId, update, data.enabled)
        toast.admSuccess(data.label, true, navigate)
        setIdentityData(identityData.map((d) => (d.siteId === data.siteId ? { ...d, moduleData: update, enabled: data.enabled } : d)))
      }
    } catch (_e) {
      toast.alert('An error has occurred, please retry later.')
    }
  }

  const onMultipleEdit = async (payload: MultipleEditPayload<'Identity'>) => {
    try {
      if (moduleId) {
        await byId.multipleEdit(moduleId, payload)
        toast.admSuccess(`${payload.selectedSites.length} site${payload.selectedSites.length > 1 ? 's' : ''}`, true, navigate)
        loadData()
      }
    } catch (_e) {
      toast.alert('An error has occurred, please retry later.')
    }
  }

  const loadData = async () => {
    if (moduleId && catalogById[moduleId]) {
      setPrebidModule(catalogById[moduleId])
      const res = await all.get(moduleId)
      setIdentityData(res)
    }
  }

  useEffect(() => {
    loadData()
  }, [moduleId, catalogById])

  // We keep all attributes here except the name because we don't want it to appear in the form
  const data = useMemo(
    () =>
      identityData.map((idModule) => {
        if (!idModule.moduleData) {
          return { ...idModule, moduleData: prebidModule.defaultValues }
        }

        const {
          moduleData: { name: _, ...moduleData },
          ...idData
        } = idModule as PrebidModuleDataBySite<'Identity'> & { moduleData: PrebidModuleData<'Identity'> }
        return { ...idData, moduleData }
      }),
    [identityData, prebidModule]
  )

  return (
    <PureAdmPrebidModulePage
      prebidModule={prebidModule}
      breadcrumbs={breadcrumbs}
      data={data}
      helpText={'UserSync : UserId'}
      isLoading={all.loading || isCatalogLoading}
      updateSiteData={updateSiteIdentity}
      baseCDNUrl={STATIC_ASSETS_CDN}
      onBackClick={() => navigate(-1)}
      onMultipleEdit={onMultipleEdit}
    />
  )
}
